import { Button } from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useState } from 'react';

import projectKeys from '@/api/queries/project/keyFactory';
import CreatePurchaseOrderSidebar from '@/ui/components/createPurchaseOrderSidebar/CreatePurchaseOrderSidebar';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';

import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { useDeal, useMaterialsList } from '@payaca/store/hooks/appState';
import * as materialsListActions from '@payaca/store/materialsList/materialsListActions';
import { Deal } from '@payaca/types/dealTypes';
import { MaterialsList } from '@payaca/types/materialsListTypes';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

export const CreatePurchaseOrderControl: FC<{
  projectId: Deal['id'];
  materialsListId: MaterialsList['id'];
}> = (props) => {
  const { projectId, materialsListId } = props;
  const { url } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [showCreatePurchaseOrderModal, setShowCreatePurchaseOrderModal] =
    useState(false);
  const deal = useDeal(projectId);

  const handleGetMaterialsListWithRelatedEntities = useCallback(
    (materialsListId: number) => {
      dispatch(
        materialsListActions.requestGetMaterialsListWithRelatedEntities(
          materialsListId
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    []
  );

  useEffect(() => {
    handleGetMaterialsListWithRelatedEntities(materialsListId);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'handleGetMaterialsListWithRelatedEntities', and 'materialsListId'
  }, []);

  const materialsList = useMaterialsList(materialsListId);

  const canCreatePurchaseOrder =
    materialsList &&
    !materialsList.isFullyPurchased &&
    !materialsList.isFullyPurchaseIntended;

  return (
    <>
      <PermissionGuard
        renderIfHasPermissions={[
          MaterialsListPermissions.PERSIST_PURCHASE_ORDER,
        ]}
      >
        <Button
          onClick={() => {
            history.push(`${url}/purchase-orders`);
            setShowCreatePurchaseOrderModal(true);
          }}
          size={'sm'}
          variant={'white'}
          disabled={!canCreatePurchaseOrder}
        >
          Create PO
        </Button>
      </PermissionGuard>

      <CreatePurchaseOrderSidebar
        projectId={projectId}
        materialsListId={materialsListId}
        isOpen={showCreatePurchaseOrderModal}
        onClose={() => setShowCreatePurchaseOrderModal(false)}
        onSuccess={(purchaseOrderId) => {
          setShowCreatePurchaseOrderModal(false);
          handleGetMaterialsListWithRelatedEntities(materialsListId);

          if (deal?.publicId) {
            void queryClient.invalidateQueries({
              queryKey: projectKeys.materialsListMaterials(deal.publicId),
            });
            void queryClient.invalidateQueries({
              queryKey: projectKeys.materialsListSummaryItems(deal.publicId),
            });
            void queryClient.invalidateQueries({
              queryKey: projectKeys.materialsListAuditLogs(deal.publicId),
            });
            void queryClient.invalidateQueries({
              queryKey: projectKeys.purchaseOrders(deal.publicId),
            });
          }

          history.push(
            `/purchaseOrders/${purchaseOrderId}?projectId=${projectId}`
          );
        }}
      />
    </>
  );
};
